import { useTranslation } from 'react-i18next';
import {
  ROUTE_TYPE_GUIDED_SELF_HELP,
  ROUTE_TYPE_SELF_HELP,
  ROUTE_TYPE_SELF_HELP_CLOSE_ONES,
} from '../config/constants';

export type DomainType =
  typeof ROUTE_TYPE_SELF_HELP |
  typeof ROUTE_TYPE_GUIDED_SELF_HELP |
  typeof ROUTE_TYPE_SELF_HELP_CLOSE_ONES;

// default as zelfhulp for backwards compatibility
const useRoutes = <T extends DomainType = typeof ROUTE_TYPE_SELF_HELP>(
  type: T = ROUTE_TYPE_SELF_HELP as T,
) => {
  const { t } = useTranslation();

  const guidedSelfHelpRoutes = {
    _routeType: ROUTE_TYPE_GUIDED_SELF_HELP as typeof ROUTE_TYPE_GUIDED_SELF_HELP,
    register: t('routes:zelfhulpbegeleid.register'),
    logout: t('routes:zelfhulpbegeleid.logout'),
    login: t('routes:zelfhulpbegeleid.login'),
    inviteClient: t('routes:zelfhulpbegeleid.invite_client'),
    resetPasswordRequest: t('routes:zelfhulpbegeleid.reset_password_request'),
    resetPassword: t('routes:zelfhulpbegeleid.reset_password'),
    resetPasswordLogin: t('routes:zelfhulpbegeleid.reset_password_login'),
    account: {
      base: t('routes:zelfhulpbegeleid.account.base'),
      email: t('routes:zelfhulpbegeleid.account.email'),
      password: t('routes:zelfhulpbegeleid.account.password'),
    },
    clients: t('routes:zelfhulpbegeleid.clients'),
    clientDetails: t('routes:zelfhulpbegeleid.client_details'),
    validateEmail: t('routes:zelfhulp.validate_email'),
  } as const;

  const selfHelpCloseOnesRoutes = {
    _routeType: ROUTE_TYPE_SELF_HELP_CLOSE_ONES as typeof ROUTE_TYPE_SELF_HELP_CLOSE_ONES,
    onboarding: {
      checklist: t('routes:zelfhulp.onboarding.checklist'),
      checklistHti: t('routes:zelfhulp.onboarding.checklist_hti'),
      credentials: t('routes:zelfhulp.onboarding.credentials'),
      credentialsHti: t('routes:zelfhulp.onboarding.credentials_hti'),
    },
    login: t('routes:zelfhulp.login'),
    logout: t('routes:zelfhulp.logout'),
    forgotPassword: t('routes:zelfhulp.forgot_password'),
    resetPasswordRequest: t('routes:zelfhulp.reset_password_request'),
    resetPassword: t('routes:zelfhulp.reset_password'),
    resetPasswordLogin: t('routes:zelfhulp.reset_password_login'),
    program: t('routes:zelfhulp.program'),
    help: t('routes:zelfhulp.help'),
    forum: {
      overview: t('routes:zelfhulp.forum.overview'),
      create_post: t('routes:zelfhulp.forum.create_post'),
    },
    diary: {
      overview: t('routes:zelfhulp.diary.overview'),
      add: t('routes:zelfhulp.diary.add'),
    },
    account: {
      base: t('routes:zelfhulp.account.base'),
      email: t('routes:zelfhulp.account.email'),
      password: t('routes:zelfhulp.account.password'),
      messages: t('routes:zelfhulp.account.messages'),
      avatar: t('routes:zelfhulp.account.avatar'),
    },
    privacy: t('routes:zelfhulp.privacy'),
    forumRegulations: t('routes:zelfhulp.forum_regulations'),
    validateEmail: t('routes:zelfhulp.validate_email'),
  } as const;

  const selfHelpRoutes = {
    _routeType: ROUTE_TYPE_SELF_HELP as typeof ROUTE_TYPE_SELF_HELP,
    onboarding: {
      checklist: t('routes:zelfhulp.onboarding.checklist'),
      aida: t('routes:zelfhulp.onboarding.aida'),
      aidaConsent: t('routes:zelfhulp.onboarding.aida_consent'),
      buddyCo: t('routes:zelfhulp.onboarding.buddy_co'),
      buddyCoConsent: t('routes:zelfhulp.onboarding.buddy_co_consent'),
      checklistHti: t('routes:zelfhulp.onboarding.checklist_hti'),
      credentials: t('routes:zelfhulp.onboarding.credentials'),
      credentialsHti: t('routes:zelfhulp.onboarding.credentials_hti'),
    },
    landing: t('routes:zelfhulp.landing'),
    login: t('routes:zelfhulp.login'),
    logout: t('routes:zelfhulp.logout'),
    forgotPassword: t('routes:zelfhulp.forgot_password'),
    resetPasswordRequest: t('routes:zelfhulp.reset_password_request'),
    resetPassword: t('routes:zelfhulp.reset_password'),
    resetPasswordLogin: t('routes:zelfhulp.reset_password_login'),
    program: t('routes:zelfhulp.program'),
    recoveryPlan: t('routes:zelfhulp.recovery_plan'),
    setRecoveryPlanGoals: t('routes:zelfhulp.set_recovery_plan_goals'),
    changeRecoveryPlan: t('routes:zelfhulp.change_recovery_plan'),
    dailyRegistration: t('routes:zelfhulp.daily_registration'),
    dailyRegistrationUpdate: t('routes:zelfhulp.daily_registration_update'),
    help: t('routes:zelfhulp.help'),
    forum: {
      overview: t('routes:zelfhulp.forum.overview'),
      create_post: t('routes:zelfhulp.forum.create_post'),
    },
    diary: {
      overview: t('routes:zelfhulp.diary.overview'),
      add: t('routes:zelfhulp.diary.add'),
    },
    badges: t('routes:zelfhulp.badges'),
    account: {
      base: t('routes:zelfhulp.account.base'),
      email: t('routes:zelfhulp.account.email'),
      password: t('routes:zelfhulp.account.password'),
      messages: t('routes:zelfhulp.account.messages'),
      avatar: t('routes:zelfhulp.account.avatar'),
      nursePractitioner: t('routes:zelfhulp.account.nurse_practitioner'),
      buddies: t('routes:zelfhulp.account.buddies'),
    },
    privacy: t('routes:zelfhulp.privacy'),
    forumRegulations: t('routes:zelfhulp.forum_regulations'),
    buddyInviteAccept: t('routes:zelfhulp.buddy_invite_accept'),
    buddyInviteRefuse: t('routes:zelfhulp.buddy_invite_refuse'),
    buddyUnsubscribe: t('routes:zelfhulp.buddy_unsubscribe'),
    validateEmail: t('routes:zelfhulp.validate_email'),
    joyride: t('routes:zelfhulp.joyride'),
    calendar: t('routes:zelfhulp.calendar'),
  } as const;

  const routes = {
    [ROUTE_TYPE_SELF_HELP]: selfHelpRoutes,
    [ROUTE_TYPE_GUIDED_SELF_HELP]: guidedSelfHelpRoutes,
    [ROUTE_TYPE_SELF_HELP_CLOSE_ONES]: selfHelpCloseOnesRoutes,
  } as const;

  return routes[type];
};

export default useRoutes;
